import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';
import LazyImg from '../../components/base/LazyImg';
import { supabase, getTenantMatchCount } from '../../lib/supabase';
import type { Property } from '../../lib/supabase';
import { useMobileTheme, getMobilePageTheme } from '../../hooks/useMobileTheme';

const REGIONS = [
  { id: 1, name: '台北市', icon: 'ri-building-line' },
  { id: 2, name: '新北市', icon: 'ri-community-line' },
  { id: 3, name: '桃園市', icon: 'ri-home-4-line' },
  { id: 4, name: '台中市', icon: 'ri-home-5-line' },
  { id: 5, name: '台南市', icon: 'ri-home-6-line' },
  { id: 6, name: '高雄市', icon: 'ri-home-7-line' },
];

const PLACEHOLDER_IMAGES = [
  'https://public.readdy.ai/ai/img_res/04d86ac4e8b2bc4bebf646b2da0faef6.jpg',
  'https://readdy.ai/api/search-image?query=cozy%20living%20room%20apartment%20warm%20lighting%20comfortable%20furniture%20clean%20simple%20interior%20professional%20real%20estate%20photo%20neutral%20background&width=600&height=400&seq=ph2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=contemporary%20apartment%20bedroom%20natural%20light%20clean%20design%20simple%20furniture%20white%20walls%20professional%20photography&width=600&height=400&seq=ph3&orientation=landscape',
];

function getPlaceholderImage(id: string) {
  const idx = id.charCodeAt(0) % PLACEHOLDER_IMAGES.length;
  return PLACEHOLDER_IMAGES[idx];
}

const SORT_OPTIONS = [
  { value: 'newest', label: '最新發布', icon: 'ri-time-line' },
  { value: 'price_asc', label: '租金低→高', icon: 'ri-sort-asc' },
  { value: 'price_desc', label: '租金高→低', icon: 'ri-sort-desc' },
  { value: 'views', label: '最多瀏覽', icon: 'ri-eye-line' },
];

const AREA_OPTIONS = [
  { value: '全部', label: '全部' },
  { value: '10以下', label: '10坪以下' },
  { value: '10-20', label: '10–20坪' },
  { value: '20-30', label: '20–30坪' },
  { value: '30以上', label: '30坪以上' },
];

const PET_OPTIONS = [
  { value: '全部', label: '全部' },
  { value: '可養寵物', label: '可養寵物', icon: 'ri-bear-smile-line' },
  { value: '不可養寵物', label: '不可養寵物', icon: 'ri-close-circle-line' },
];

const WATER_OPTIONS = [
  { value: '全部', label: '全部' },
  { value: '含水費', label: '含水費', icon: 'ri-drop-line' },
  { value: '不含水費', label: '不含水費' },
];

const ELECTRIC_OPTIONS = [
  { value: '全部', label: '全部' },
  { value: '含電費', label: '含電費', icon: 'ri-flashlight-line' },
  { value: '不含電費', label: '不含電費' },
  { value: '台電計費', label: '台電計費' },
];

export default function Regions() {
  const [searchParams] = useSearchParams();
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [priceRange, setPriceRange] = useState<string>('全部');
  const [roomType, setRoomType] = useState<string>('全部');
  const [areaRange, setAreaRange] = useState<string>('全部');
  const [petPolicy, setPetPolicy] = useState<string>('全部');
  const [waterBill, setWaterBill] = useState<string>('全部');
  const [electricBill, setElectricBill] = useState<string>('全部');
  const [keyword, setKeyword] = useState<string>('');
  const [inputKeyword, setInputKeyword] = useState<string>('');
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<string>('newest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tenantMatchCount, setTenantMatchCount] = useState(64);
  const { theme } = useMobileTheme();
  const pt = getMobilePageTheme(theme);

  useEffect(() => {
    getTenantMatchCount().then(count => setTenantMatchCount(64 + count));
  }, []);

  useEffect(() => {
    const urlRegion = searchParams.get('region');
    const urlRoomType = searchParams.get('roomType');
    const urlKeyword = searchParams.get('keyword');
    if (urlRegion) setSelectedRegion(urlRegion);
    if (urlRoomType) setRoomType(urlRoomType);
    if (urlKeyword) { setKeyword(urlKeyword); setInputKeyword(urlKeyword); }
  }, [searchParams]);

  useEffect(() => {
    fetchProperties();
  }, [selectedRegion, priceRange, roomType, areaRange, petPolicy, waterBill, electricBill, keyword, sortBy]);

  const fetchProperties = async () => {
    setLoading(true);
    let query = supabase.from('properties').select('*').eq('status', 'active');

    if (selectedRegion) query = query.ilike('location', `${selectedRegion}%`);
    if (keyword) query = query.or(`title.ilike.%${keyword}%,description.ilike.%${keyword}%,location.ilike.%${keyword}%`);

    if (priceRange === '10000以下') query = query.lt('price', 10000);
    else if (priceRange === '10000-20000') query = query.gte('price', 10000).lte('price', 20000);
    else if (priceRange === '20000-30000') query = query.gte('price', 20000).lte('price', 30000);
    else if (priceRange === '30000以上') query = query.gt('price', 30000);

    if (roomType === '1房') query = query.eq('rooms', 1);
    else if (roomType === '2房') query = query.eq('rooms', 2);
    else if (roomType === '3房') query = query.eq('rooms', 3);
    else if (roomType === '4房以上') query = query.gte('rooms', 4);

    if (areaRange === '10以下') query = query.lt('area', 10);
    else if (areaRange === '10-20') query = query.gte('area', 10).lte('area', 20);
    else if (areaRange === '20-30') query = query.gte('area', 20).lte('area', 30);
    else if (areaRange === '30以上') query = query.gt('area', 30);

    if (petPolicy !== '全部') query = query.eq('pet_allowed', petPolicy);
    if (waterBill !== '全部') query = query.eq('water_bill', waterBill);
    if (electricBill !== '全部') query = query.eq('electric_bill', electricBill);

    if (sortBy === 'newest') query = query.order('created_at', { ascending: false });
    else if (sortBy === 'price_asc') query = query.order('price', { ascending: true });
    else if (sortBy === 'price_desc') query = query.order('price', { ascending: false });
    else if (sortBy === 'views') query = query.order('view_count', { ascending: false });

    const { data } = await query;
    setProperties(data || []);
    setLoading(false);
  };

  const handleKeywordSearch = () => setKeyword(inputKeyword);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') handleKeywordSearch(); };

  const clearAll = () => {
    setSelectedRegion(null);
    setPriceRange('全部');
    setRoomType('全部');
    setAreaRange('全部');
    setPetPolicy('全部');
    setWaterBill('全部');
    setElectricBill('全部');
    setKeyword('');
    setInputKeyword('');
    setSortBy('newest');
  };

  const advancedActiveCount = [
    areaRange !== '全部',
    petPolicy !== '全部',
    waterBill !== '全部',
    electricBill !== '全部',
  ].filter(Boolean).length;

  const currentSort = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];

  const activeTags: { label: string; onRemove: () => void }[] = [];
  if (keyword) activeTags.push({ label: `關鍵字：${keyword}`, onRemove: () => { setKeyword(''); setInputKeyword(''); } });
  if (selectedRegion) activeTags.push({ label: selectedRegion, onRemove: () => setSelectedRegion(null) });
  if (priceRange !== '全部') activeTags.push({ label: `租金 ${priceRange}`, onRemove: () => setPriceRange('全部') });
  if (roomType !== '全部') activeTags.push({ label: roomType, onRemove: () => setRoomType('全部') });
  if (areaRange !== '全部') activeTags.push({ label: `${AREA_OPTIONS.find(a => a.value === areaRange)?.label}`, onRemove: () => setAreaRange('全部') });
  if (petPolicy !== '全部') activeTags.push({ label: petPolicy, onRemove: () => setPetPolicy('全部') });
  if (waterBill !== '全部') activeTags.push({ label: waterBill, onRemove: () => setWaterBill('全部') });
  if (electricBill !== '全部') activeTags.push({ label: electricBill, onRemove: () => setElectricBill('全部') });

  return (
    <div className={`min-h-screen ${pt.pageBg}`}>
      <SeoHead
        title="台灣租屋找房｜台北 新北 桃園 台中 台南 高雄 屋主直租零仲介費 - 藍舍AI地產"
        description="搜尋台灣六都屋主直租房源，台北市、新北市、桃園市、台中市、台南市、高雄最新房源，依租金、房型、坪數、寵物政策篩選，零仲介費直接聯繫屋主，租屋省錢首選。"
        keywords="台灣租屋,找房,屋主直租,零仲介費,台北租屋,台北市租屋,大安區租屋,信義區租屋,中山區租屋,新北租屋,新北市租屋,板橋租屋,新莊租屋,新店租屋,桃園租屋,桃園市租屋,中壢租屋,台中租屋,台中市租屋,西屯區租屋,北屯區租屋,台南租屋,台南市租屋,東區租屋,永康區租屋,高雄租屋,高雄市租屋,苓雅區租屋,三民區租屋,免仲介費租屋,租房找屋主,AI租屋平台"
        canonical="/regions"
        lastModified="2026-03-18"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SearchResultsPage",
          "name": "台灣六都租屋找房 - 藍舍AI地產",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://ailanshe.com'}/regions`,
          "description": "搜尋台灣六都零仲介費屋主直租房源，台北、新北、桃園、台中、台南、高雄最新房源",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "首頁", "item": `${import.meta.env.VITE_SITE_URL || 'https://ailanshe.com'}/`},
              {"@type": "ListItem", "position": 2, "name": "六都房源搜尋", "item": `${import.meta.env.VITE_SITE_URL || 'https://ailanshe.com'}/regions`}
            ]
          }
        }}
      />
      <Navbar />

      <div className="relative h-44 md:h-64 flex items-end overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20vibrant%20Taiwan%20city%20neighborhood%20with%20dense%20residential%20buildings%20colorful%20rooftops%20and%20tree-lined%20streets%2C%20warm%20afternoon%20sunlight%20casting%20golden%20shadows%2C%20urban%20landscape%20photography%20from%20above%20showing%20diverse%20housing%20districts%2C%20clear%20blue%20sky%20with%20soft%20clouds%2C%20modern%20and%20traditional%20architecture%20mix&width=1920&height=500&seq=regions-hero1&orientation=landscape"
          alt="找房背景"
          className="hidden md:block absolute inset-0 w-full h-full object-cover object-top"
        />
        {/* 手機版純色背景 */}
        <div className={`md:hidden absolute inset-0 ${theme === 'tech' ? 'bg-gray-900' : theme === 'pink' ? 'bg-rose-600' : 'bg-teal-700'}`} />
        <div
          className="absolute inset-0"
          style={{ background: pt.heroOverlay }}
        ></div>
        {/* tech 主題：掃描線光暈裝飾 */}
        {theme === 'tech' && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950 to-transparent"></div>
            <div className="absolute inset-0" style={{ background: 'repeating-linear-gradient(0deg,transparent,transparent 40px,rgba(45,212,191,.015) 40px,rgba(45,212,191,.016) 41px)' }}></div>
          </div>
        )}
        {/* pink 主題：柔和光暈 */}
        {theme === 'pink' && (
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-rose-100/60 to-transparent"></div>
        )}
        <div className="relative z-10 w-full pb-7 md:pb-10 pt-20 md:pt-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-black text-white mb-2 md:mb-3 drop-shadow-lg">找房搜尋</h1>
            <p className="text-sm md:text-lg text-white/90 drop-shadow">選擇地區、房型或輸入關鍵字，快速找到理想房源</p>
          </div>
        </div>
      </div>

      <div className="pb-16 md:pb-20 relative">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
          {/* 關鍵字搜尋 */}
          <div className={`${pt.filterCardBg} border ${pt.filterCardBorder} rounded-2xl shadow-sm p-3 md:p-4 mb-5 md:mb-8 flex items-center gap-2 md:gap-3`}>
            <i className={`ri-search-line ${pt.iconColor} text-lg flex-shrink-0 w-5 h-5 flex items-center justify-center`}></i>
            <input
              type="text" value={inputKeyword} onChange={(e) => setInputKeyword(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="輸入關鍵字搜尋，如：捷運、近學校..."
              className={`flex-1 text-sm ${pt.searchText} outline-none min-w-0`}
              style={{ background: 'transparent' }}
            />
            {inputKeyword && (
              <button onClick={() => { setInputKeyword(''); setKeyword(''); }} className={`${pt.textSecondary} hover:text-red-400 cursor-pointer w-5 h-5 flex items-center justify-center flex-shrink-0`}>
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
            <button onClick={handleKeywordSearch} className={`px-4 md:px-6 py-2 ${pt.primaryBtn} rounded-xl text-sm font-semibold whitespace-nowrap cursor-pointer flex-shrink-0`}>
              搜尋
            </button>
          </div>

          {/* 區域選擇 */}
          <div className="mb-6 md:mb-8">
            <h2 className={`text-lg md:text-xl font-bold ${pt.sectionAccent} mb-3 md:mb-4`}>熱門區域</h2>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-4">
              {REGIONS.map((region) => (
                <button
                  key={region.id}
                  onClick={() => setSelectedRegion(selectedRegion === region.name ? null : region.name)}
                  className={`p-3 md:p-5 rounded-2xl transition-all cursor-pointer border ${selectedRegion === region.name ? `${pt.filterActive} border-transparent shadow-lg scale-105` : `${pt.filterCardBg} ${pt.filterCardBorder} ${pt.textPrimary} hover:shadow-md`}`}
                >
                  <div className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center mx-auto mb-1.5 md:mb-2 rounded-full ${selectedRegion === region.name ? 'bg-white/20' : pt.iconBg}`}>
                    <i className={`${region.icon} text-lg md:text-xl ${selectedRegion === region.name ? 'text-white' : pt.iconColor}`}></i>
                  </div>
                  <h3 className="font-bold text-xs md:text-sm mb-0.5 md:mb-1">{region.name}</h3>
                  <p className={`text-xs hidden md:block ${selectedRegion === region.name ? 'text-white/80' : pt.textSecondary}`}>點擊篩選</p>
                </button>
              ))}
            </div>
          </div>

          {/* 篩選條件 */}
          <div className={`${pt.filterCardBg} border ${pt.filterCardBorder} rounded-2xl shadow-sm mb-5 md:mb-8 overflow-hidden`}>
            <div className="p-4 md:p-6">
              <div className="flex flex-col gap-4">
                {/* 價格 */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3">
                  <span className={`text-sm font-semibold ${pt.filterLabelColor} whitespace-nowrap w-20 flex-shrink-0`}>
                    <i className={`ri-money-dollar-circle-line mr-1 ${pt.iconColor}`}></i>租金
                  </span>
                  <div className="flex gap-1.5 flex-wrap">
                    {['全部', '10000以下', '10000-20000', '20000-30000', '30000以上'].map((range) => (
                      <button key={range} onClick={() => setPriceRange(range)}
                        className={`px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border ${priceRange === range ? pt.filterActive : pt.filterInactive}`}>
                        {range}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={`h-px ${pt.dividerColor}`}></div>

                {/* 房型 */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3">
                  <span className={`text-sm font-semibold ${pt.filterLabelColor} whitespace-nowrap w-20 flex-shrink-0`}>
                    <i className={`ri-door-line mr-1 ${pt.iconColor}`}></i>房型
                  </span>
                  <div className="flex gap-1.5 flex-wrap">
                    {['全部', '1房', '2房', '3房', '4房以上'].map((type) => (
                      <button key={type} onClick={() => setRoomType(type)}
                        className={`px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border ${roomType === type ? pt.filterActive : pt.filterInactive}`}>
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 進階篩選展開按鈕 */}
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className={`flex items-center gap-2 text-sm font-semibold ${pt.sectionAccent} hover:opacity-80 cursor-pointer w-fit transition-colors`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-equalizer-line text-sm"></i>
                  </div>
                  進階篩選
                  {advancedActiveCount > 0 && (
                    <span className={`px-1.5 py-0.5 ${pt.tagBg} text-white text-xs rounded-full leading-none`}>{advancedActiveCount}</span>
                  )}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-arrow-${showAdvanced ? 'up' : 'down'}-s-line text-sm`}></i>
                  </div>
                </button>
              </div>
            </div>

            {showAdvanced && (
              <div className={`border-t ${pt.filterCardBorder} ${theme === 'tech' ? 'bg-gray-900/60' : theme === 'pink' ? 'bg-rose-50/60' : 'bg-stone-50'} px-4 md:px-6 py-4 md:py-5 flex flex-col gap-4`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3">
                  <span className={`text-sm font-semibold ${pt.filterLabelColor} whitespace-nowrap w-20 flex-shrink-0`}>
                    <i className={`ri-ruler-line mr-1 ${pt.iconColor}`}></i>坪數
                  </span>
                  <div className="flex gap-1.5 flex-wrap">
                    {AREA_OPTIONS.map((opt) => (
                      <button key={opt.value} onClick={() => setAreaRange(opt.value)}
                        className={`px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border ${areaRange === opt.value ? pt.filterActive : pt.filterInactive}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={`h-px ${pt.dividerColor}`}></div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3">
                  <span className={`text-sm font-semibold ${pt.filterLabelColor} whitespace-nowrap w-20 flex-shrink-0`}>
                    <i className={`ri-bear-smile-line mr-1 ${pt.iconColor}`}></i>寵物
                  </span>
                  <div className="flex gap-1.5 flex-wrap">
                    {PET_OPTIONS.map((opt) => (
                      <button key={opt.value} onClick={() => setPetPolicy(opt.value)}
                        className={`px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 border ${petPolicy === opt.value ? pt.filterActive : pt.filterInactive}`}>
                        {opt.icon && <i className={`${opt.icon} text-xs`}></i>}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={`h-px ${pt.dividerColor}`}></div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3">
                  <span className={`text-sm font-semibold ${pt.filterLabelColor} whitespace-nowrap w-20 flex-shrink-0`}>
                    <i className={`ri-drop-line mr-1 ${pt.iconColor}`}></i>水費
                  </span>
                  <div className="flex gap-1.5 flex-wrap">
                    {WATER_OPTIONS.map((opt) => (
                      <button key={opt.value} onClick={() => setWaterBill(opt.value)}
                        className={`px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 border ${waterBill === opt.value ? pt.filterActive : pt.filterInactive}`}>
                        {opt.icon && <i className={`${opt.icon} text-xs`}></i>}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={`h-px ${pt.dividerColor}`}></div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3">
                  <span className={`text-sm font-semibold ${pt.filterLabelColor} whitespace-nowrap w-20 flex-shrink-0`}>
                    <i className={`ri-flashlight-line mr-1 ${pt.iconColor}`}></i>電費
                  </span>
                  <div className="flex gap-1.5 flex-wrap">
                    {ELECTRIC_OPTIONS.map((opt) => (
                      <button key={opt.value} onClick={() => setElectricBill(opt.value)}
                        className={`px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 border ${electricBill === opt.value ? pt.filterActive : pt.filterInactive}`}>
                        {opt.icon && <i className={`${opt.icon} text-xs`}></i>}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {advancedActiveCount > 0 && (
                  <button
                    onClick={() => { setAreaRange('全部'); setPetPolicy('全部'); setWaterBill('全部'); setElectricBill('全部'); }}
                    className={`text-xs ${pt.textSecondary} hover:text-red-500 underline cursor-pointer w-fit transition-colors`}
                  >
                    清除進階篩選
                  </button>
                )}
              </div>
            )}
          </div>

          {/* 結果數量 + 排序 */}
          <div className="flex items-center justify-between mb-4 md:mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-1.5 flex-wrap">
              {!loading && (
                <p className={`text-sm ${pt.textSecondary}`}>
                  共找到 <span className={`font-bold ${pt.accentText}`}>{properties.length}</span> 筆房源
                </p>
              )}
              {activeTags.length > 0 && (
                <div className="flex items-center gap-1.5 mb-1.5 sm:hidden flex-wrap">
                  {activeTags.map((tag, i) => (
                    <span key={i} className={`px-2 py-0.5 ${pt.tagBg} text-white text-xs font-medium rounded-full whitespace-nowrap flex items-center gap-1.5`}>
                      {tag.label}
                      <button onClick={tag.onRemove} className="hover:text-white/80 cursor-pointer">
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  ))}
                  <button onClick={clearAll} className={`text-xs ${pt.textSecondary} hover:text-red-500 underline cursor-pointer whitespace-nowrap transition-colors`}>
                    清除全部
                  </button>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className={`flex items-center gap-2 px-4 py-2 ${pt.filterCardBg} border ${pt.filterCardBorder} rounded-xl text-sm font-medium ${pt.filterLabelColor} transition-colors cursor-pointer shadow-sm whitespace-nowrap`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${currentSort.icon} text-sm`}></i>
                </div>
                <span>{currentSort.label}</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-${showSortDropdown ? 'up' : 'down'}-s-line text-sm`}></i>
                </div>
              </button>
              {showSortDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowSortDropdown(false)}></div>
                  <div className={`absolute right-0 top-full mt-2 w-44 ${pt.filterCardBg} rounded-2xl shadow-xl border ${pt.filterCardBorder} overflow-hidden z-20`}>
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => { setSortBy(option.value); setShowSortDropdown(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer whitespace-nowrap ${sortBy === option.value ? `${pt.accentText} font-semibold` : `${pt.filterLabelColor} hover:${theme === 'tech' ? 'bg-gray-700' : 'bg-gray-50'}`}`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${option.icon} text-sm`}></i>
                        </div>
                        {option.label}
                        {sortBy === option.value && (
                          <div className="w-4 h-4 flex items-center justify-center ml-auto">
                            <i className={`ri-check-line ${pt.accentText} text-sm`}></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {loading && (
            <div className={`flex flex-col items-center justify-center py-20 ${pt.filterCardBg} border ${pt.filterCardBorder} rounded-3xl shadow-sm`}>
              <i className={`ri-loader-4-line text-4xl ${pt.loadingColor} animate-spin mb-4`}></i>
              <p className={pt.textSecondary}>載入房源中...</p>
            </div>
          )}

          {/* 有房源 */}
          {!loading && properties.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {properties.map((property) => {
                const coverImg = property.photos && property.photos.length > 0
                  ? property.photos[0]
                  : getPlaceholderImage(property.id);
                const coverPos = (property.photo_positions && property.photo_positions[0]) ? property.photo_positions[0] : '50% 50%';
                const coverZoom = (property.photo_zooms && property.photo_zooms[0]) ? property.photo_zooms[0] : 1;
                return (
                  <Link
                    key={property.id}
                    to={`/property/${property.id}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all group cursor-pointer flex flex-col"
                  >
                    {/* 圖片區：固定高度，不再跟著內容變動 */}
                    <div className="relative w-full h-44 sm:h-48 flex-shrink-0 overflow-hidden bg-gray-100">
                      <LazyImg
                        src={coverImg}
                        alt={property.title}
                        containerClassName="w-full h-full"
                        imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        imgStyle={{ objectPosition: coverPos }}
                      />
                      <span className="absolute top-2.5 left-2.5 px-2.5 py-1 bg-teal-500 text-white text-xs font-medium rounded-full whitespace-nowrap">零仲介費</span>
                      {property.pet_allowed === '可養寵物' && (
                        <span className="absolute bottom-2.5 left-2.5 px-2 py-0.5 bg-amber-400/90 text-white text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-bear-smile-line text-xs"></i>可養寵物
                        </span>
                      )}
                    </div>

                    {/* 內容區 */}
                    <div className="flex-1 p-3.5 md:p-5 flex flex-col">
                      <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 mb-1.5">{property.title}</h3>
                      <p className="text-xs text-gray-500 mb-2.5 flex items-center gap-1">
                        <i className="ri-map-pin-line text-teal-500 flex-shrink-0"></i>
                        <span className="truncate">{property.location}</span>
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3 flex-wrap">
                        <span className="flex items-center gap-0.5 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">
                          <i className="ri-ruler-line text-gray-400"></i>{property.area}坪
                        </span>
                        <span className="flex items-center gap-0.5 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">
                          {['店面', '廠房', '辦公室'].includes(property.property_type || '')
                            ? <><i className="ri-store-2-line text-gray-400"></i>{property.property_type}</>
                            : <><i className="ri-door-line text-gray-400"></i>{property.rooms}房{property.bathrooms}衛</>
                          }
                        </span>
                      </div>
                      <div className="flex items-end justify-between gap-1 mt-auto">
                        <div>
                          <span className="text-base font-black text-red-500 leading-none">NT$ {property.price.toLocaleString()}</span>
                          <span className="text-xs font-normal text-gray-400">/月</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <i className="ri-eye-line"></i>
                          <span>{property.view_count || 0}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {!loading && properties.length === 0 && (
            <div className={`flex flex-col items-center justify-center py-16 md:py-24 ${pt.filterCardBg} border ${pt.filterCardBorder} rounded-3xl shadow-sm`}>
              <div className={`w-16 h-16 md:w-20 md:h-20 flex items-center justify-center ${pt.emptyIconBg} rounded-full mb-5 md:mb-6`}>
                <i className={`ri-home-4-line text-3xl md:text-4xl ${pt.emptyIconColor}`}></i>
              </div>
              <p className={`text-lg md:text-xl font-bold ${pt.textPrimary} mb-2`}>目前尚無符合條件的房源</p>
              <p className={`text-sm ${pt.textSecondary} mb-6 md:mb-8`}>試試調整篩選條件，或成為第一個發布房源的屋主！</p>
              {activeTags.length > 0 && (
                <button onClick={clearAll} className={`mb-4 px-6 py-2 border ${theme === 'pink' ? 'border-rose-300 text-rose-500 hover:bg-rose-50' : theme === 'tech' ? 'border-teal-500/50 text-teal-400 hover:bg-teal-500/10' : 'border-teal-400 text-teal-600 hover:bg-teal-50'} rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap`}>
                  清除所有篩選
                </button>
              )}
              <Link to="/publish" className={`px-7 md:px-8 py-3 ${pt.primaryBtn} rounded-full font-semibold whitespace-nowrap cursor-pointer`}>
                立即發布房源
              </Link>
            </div>
          )}

          {/* 發布房源提示 */}
          <div className={`mt-10 md:mt-16 bg-gradient-to-br ${pt.ctaGradient} rounded-3xl p-8 md:p-12 text-white text-center`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">您是屋主嗎？</h2>
            <p className="text-base md:text-lg mb-6 md:mb-8 opacity-90">免費發布您的房源，直接與租客溝通</p>
            <Link to="/publish" className="inline-block px-8 md:px-10 py-3 md:py-4 bg-white text-teal-600 rounded-full font-bold text-base md:text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
              立即發布房源
            </Link>
          </div>
        </div>
      </div>

      {/* ── AI 媒合橫幅 ── */}
      <section className={`py-10 bg-gradient-to-r ${pt.ctaGradient}`}>
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-2xl flex-shrink-0">
                <i className="ri-sparkling-2-fill text-2xl text-amber-300"></i>
              </div>
              <div>
                <h3 className="text-white font-black text-lg leading-tight">找不到理想房？讓 AI 幫你等</h3>
                <p className="text-white/75 text-sm mt-0.5">填寫一次需求，有新房源上架立即通知你 · 完全免費</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 text-white/70 text-xs">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400"></span>
                </span>
                {tenantMatchCount} 位租客已提交需求
              </div>
              <Link
                to="/ai-match"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-teal-600 rounded-full font-black text-sm hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-robot-2-line text-base"></i>
                提交 AI 媒合需求
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
